import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { FileSignature, Receipt, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { fmtTs } from '../utils/time';

/**
 * "Signed & paid" on the customer card (owner, Oct 26 2026): every bid they
 * signed online and every bill they paid, newest first, read-only. Open bills
 * stay on Billing — only their balance shows here. GET /customers/:id/signed-and-paid.
 */
type SignedBid = {
  id: number; title: string; kind: string; total: number; total_words: string;
  signed_by: string | null; signed_at: string | null; status: string; now: string;
  job_id: number | null; open_change_orders: number; change_order_words: string | null; share_token: string | null;
};
type PaidBill = {
  id: number; number: string; paid_at: string | null; paid_on: string | null; amount: number; amount_words: string;
  how: string; covered: string; tip: number; deposit: boolean; quote_id: number | null; share_token: string | null;
};
type Data = { signed_bids: SignedBid[]; paid_bills: PaidBill[]; open_balance: number; open_count: number; signed_total: number; paid_total: number };

const HOW: Record<string, string> = { card: 'Card', autopay: 'Autopay', zelle: 'Zelle', venmo: 'Venmo', cash: 'Cash', check: 'Check', jobber: 'Jobber', other: 'Other' };
const SHOW = 4;   // rows before "Show all"

function CopyLink({ url, title }: { url: string; title: string }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" title={title}
      onClick={async e => {
        e.stopPropagation();
        try { await navigator.clipboard.writeText(url); setDone(true); setTimeout(() => setDone(false), 1500); }
        catch { window.prompt('Copy this link:', url); }
      }}
      className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-brand-700">
      {done ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}{done ? 'Copied' : 'Copy link'}
    </button>
  );
}

export default function SignedAndPaidBlock({ customerId, onNavigate }: { customerId: number; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const [allBids, setAllBids] = useState(false);
  const [allBills, setAllBills] = useState(false);
  const { data, isError } = useQuery(['signed-and-paid', customerId],
    () => api.get(`/customers/${customerId}/signed-and-paid`).then(r => r.data as Data),
    { retry: false, staleTime: 30_000 });
  if (isError) { toast.error('Could not load their signed bids and paid bills'); return null; }
  if (!data) return null;
  const bids = allBids ? data.signed_bids : data.signed_bids.slice(0, SHOW);
  const bills = allBills ? data.paid_bills : data.paid_bills.slice(0, SHOW);
  const go = (path: string) => { onNavigate?.(); navigate(path); };
  const origin = window.location.origin;
  const nothing = !data.signed_bids.length && !data.paid_bills.length;

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-white px-3 py-2">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-gray-900">Signed &amp; paid</p>
        <span className="text-xs text-gray-500">
          {data.signed_bids.length} signed bid{data.signed_bids.length === 1 ? '' : 's'} · {data.paid_bills.length} paid bill{data.paid_bills.length === 1 ? '' : 's'}
        </span>
        {data.open_count > 0 && (
          <button type="button" onClick={() => go('/billing')} title="Bills sent and not yet paid — on Billing"
            className="ml-auto text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 hover:bg-amber-100">
            Open balance ${data.open_balance.toFixed(2)}
          </button>
        )}
      </div>
      {nothing && <p className="text-xs text-gray-500 mt-1">Nothing signed or paid yet.</p>}

      {data.signed_bids.length > 0 && (
        <div className="mt-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1"><FileSignature className="h-3 w-3" /> Signed bids</p>
          <ul className="mt-1 space-y-1">
            {bids.map(b => (
              <li key={b.id} onClick={() => go(`/quotes?open=${b.id}`)} title="Open this bid"
                className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 cursor-pointer hover:bg-brand-50">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900 truncate">{b.title}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-brand-800">{b.kind}</span>
                      {b.open_change_orders > 0 && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800" title={b.change_order_words || ''}>
                          {b.open_change_orders} open change order{b.open_change_orders === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600">
                      Signed by {b.signed_by || 'them'} · {fmtTs(b.signed_at, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                      <span>{b.now}</span>
                      {b.job_id && (
                        <button type="button" onClick={e => { e.stopPropagation(); go(`/jobs/${b.job_id}`); }}
                          className="inline-flex items-center gap-0.5 text-[11px] text-brand-700 hover:underline"><ExternalLink className="h-3 w-3" />job</button>
                      )}
                      {b.share_token && <CopyLink url={`${origin}/q/${b.share_token}`} title="Copy the link to the bid they signed" />}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-green-700 whitespace-nowrap">{b.total_words}</span>
                </div>
              </li>
            ))}
          </ul>
          {data.signed_bids.length > SHOW && (
            <button type="button" onClick={() => setAllBids(v => !v)} className="text-xs text-brand-700 hover:underline mt-1">
              {allBids ? 'Show fewer' : `Show all ${data.signed_bids.length}`}
            </button>
          )}
        </div>
      )}

      {data.paid_bills.length > 0 && (
        <div className="mt-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1"><Receipt className="h-3 w-3" /> Paid bills <span className="normal-case font-normal">· {`$${data.paid_total.toFixed(2)}`} in all</span></p>
          <ul className="mt-1 space-y-1">
            {bills.map(b => (
              <li key={b.id} onClick={() => go(`/billing?invoice=${b.id}`)} title="Open this bill"
                className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 cursor-pointer hover:bg-brand-50">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">{b.number}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-green-100 text-green-700">paid</span>
                      {b.deposit && <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-800">Deposit</span>}
                      {b.tip > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">tip ${b.tip.toFixed(2)}</span>}
                    </div>
                    <p className="text-xs text-gray-600">
                      Paid {b.paid_on ? fmtTs(b.paid_at, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} · {HOW[b.how] || b.how}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                      {b.covered && <span className="truncate">{b.covered}</span>}
                      {b.share_token && <CopyLink url={`${origin}/invoice/${b.share_token}`} title="Copy the link to their bill" />}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-green-700 whitespace-nowrap">{b.amount_words}</span>
                </div>
              </li>
            ))}
          </ul>
          {data.paid_bills.length > SHOW && (
            <button type="button" onClick={() => setAllBills(v => !v)} className="text-xs text-brand-700 hover:underline mt-1">
              {allBills ? 'Show fewer' : `Show all ${data.paid_bills.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
